import PageGlow from "../components/PageGlow";
import { useEffect, useLayoutEffect, useState, useCallback, useRef, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Search, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, SlidersHorizontal, X, Info } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import JobCard from "../components/JobCard";
import LocationInput from "../components/LocationInput";
import DepartmentInput from "../components/DepartmentInput";
import { JobAlertControl } from "../components/JobAlertControl";
import JobDetail from "./JobDetail";
import ExtJobDetail from "./ExtJobDetail";

export default function Jobs() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ categories: [], job_types: [], workplaces: [] });
  const [facets, setFacets] = useState({ locations: [], categories: [], job_types: [] });
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [showAllAreas, setShowAllAreas] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [query, setQuery] = useState(searchParams.get("query") || "");
  const [location, setLocation] = useState(searchParams.get("location") || "");
  const [areas, setAreas] = useState([]);
  const imprKey = useRef("");
  const [liveTotal, setLiveTotal] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // modal-local filter state — the search only updates when the user presses Search
  const [fCategory, setFCategory] = useState("");
  const [fJobType, setFJobType] = useState("");
  const [advOpen, setAdvOpen] = useState(false);
  const [fWorkplace, setFWorkplace] = useState("");
  const [fRadius, setFRadius] = useState("");
  const [fSalaryPeriod, setFSalaryPeriod] = useState("year");
  const [fSalaryMin, setFSalaryMin] = useState("");
  const [fSalaryMax, setFSalaryMax] = useState("");
  const [fSalaryOnly, setFSalaryOnly] = useState(false);
  const [fSort, setFSort] = useState("");
  const resultsRef = useRef(null);

  // keep the list position so returning from a job lands exactly where you left
  useEffect(() => {
    let t;
    const onScroll = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        try { sessionStorage.setItem("ht_jobs_scroll", JSON.stringify({ key: window.location.search, y: window.scrollY })); } catch { /* scroll save is best-effort */ }
      }, 150);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); clearTimeout(t); };
  }, []);
  const FILTER_KEYS = ["query", "location", "category", "job_type", "workplace", "radius", "salary_min", "salary_max", "salary_only", "sort"];
  const activeFilterCount = ["query", "location", "category", "job_type", "workplace", "radius", "sort"].filter((k) => searchParams.get(k)).length
    + ((searchParams.get("salary_min") || searchParams.get("salary_max")) ? 1 : 0)
    + (searchParams.get("salary_only") ? 1 : 0);
  const advActiveCount = ["workplace", "radius", "sort", "salary_only"].filter((k) => searchParams.get(k)).length
    + ((searchParams.get("salary_min") || searchParams.get("salary_max")) ? 1 : 0);
  const clearFilterForm = () => {
    setQuery(""); setLocation(""); setFCategory(""); setFJobType("");
    setFWorkplace(""); setFRadius(""); setFSalaryPeriod("year");
    setFSalaryMin(""); setFSalaryMax(""); setFSalaryOnly(false); setFSort("");
  };
  const resetFilters = () => { clearFilterForm(); setSearchParams(new URLSearchParams()); };

  useEffect(() => {
    api.get("/jobs", { params: { limit: 1 } }).then((r) => setLiveTotal(r.data.total || 0)).catch(() => {});
  }, []);

  const page = parseInt(searchParams.get("page") || "1", 10);
  const hasFilters = FILTER_KEYS.some((k) => searchParams.get(k));

  const limit = parseInt(searchParams.get("limit") || "25", 10);

  // desktop split view (owner-controlled: Control Tower → Operations → Public experience).
  // "selected" in the URL drives the right pane so jobs stay shareable and Back/Forward work.
  const [layout, setLayout] = useState("list");
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    api.get("/settings/public", { params: { _: Date.now() } })  // cache-buster: layout changes must apply immediately
      .then((r) => setLayout(r.data?.desktop_layout === "split" ? "split" : "list"))
      .catch(() => {}) // any error keeps the original list view — the safe fallback
      .finally(() => setLayoutLoaded(true));
    const mq = window.matchMedia("(min-width: 1024px)");
    const fn = () => setIsDesktop(mq.matches);
    mq.addEventListener("change", fn);
    window.addEventListener("resize", fn);
    fn();
    return () => { mq.removeEventListener("change", fn); window.removeEventListener("resize", fn); };
  }, []);
  const splitActive = layout === "split" && isDesktop;
  const selectedParam = searchParams.get("selected") || "";
  const parseSelected = (v) => {
    if (!v) return null;
    if (v.startsWith("ext:")) {
      const [, src, jid] = v.split(":");
      return src && jid ? { kind: "ext", source: src, id: jid } : null;
    }
    return { kind: "native", slug: v };
  };
  const selected = splitActive ? parseSelected(selectedParam) : null;
  const listRef = useRef(null);
  const splitOn = splitActive && !!selected;
  // while the split workspace is open it covers the whole viewport below the header and the
  // page behind it is scroll-locked (layout effect — installed before paint, no gap); the
  // panes scroll themselves (overscroll contained). On close the page's scroll position is
  // exactly where the user left it — it never moved.
  useLayoutEffect(() => {
    if (!splitOn) return;
    const html = document.documentElement;
    const prev = html.style.overflow;
    html.style.overflow = "hidden";
    return () => { html.style.overflow = prev; };
  }, [splitOn]);

  // filters modal: sync the form from the current URL when it opens, lock the page
  // behind it, and let Escape close it — the page is restored exactly on close
  useEffect(() => {
    if (!filtersOpen) return;
    setQuery(searchParams.get("query") || "");
    setLocation(searchParams.get("location") || "");
    setFCategory(searchParams.get("category") || "");
    setFJobType(searchParams.get("job_type") || "");
    setFWorkplace(searchParams.get("workplace") || "");
    setFRadius(searchParams.get("radius") || "");
    setFSalaryPeriod(searchParams.get("salary_period") || "year");
    setFSalaryMin(searchParams.get("salary_min") || "");
    setFSalaryMax(searchParams.get("salary_max") || "");
    setFSalaryOnly(!!searchParams.get("salary_only"));
    setFSort(searchParams.get("sort") || "");
    if (["workplace", "radius", "salary_min", "salary_max", "salary_only", "sort"].some((k) => searchParams.get(k))) setAdvOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersOpen]);
  useLayoutEffect(() => {
    if (!filtersOpen) return;
    const html = document.documentElement;
    const prev = html.style.overflow;
    html.style.overflow = "hidden";
    return () => { html.style.overflow = prev; };
  }, [filtersOpen]);
  useEffect(() => {
    if (!filtersOpen) return;
    const esc = (e) => { if (e.key === "Escape") setFiltersOpen(false); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [filtersOpen]);
  const selectJob = (job) => {
    const params = new URLSearchParams(searchParams);
    params.set("selected", job.source && job.source !== "native" ? `ext:${job.source}:${job.id}` : job.slug);
    setSearchParams(params);
  };
  const clearSelected = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("selected");
    setSearchParams(params);
  };
  // on phones (or if split view is off) a shared split link opens the normal job page
  useEffect(() => {
    if (!layoutLoaded || splitActive || !selectedParam) return;
    const s = parseSelected(selectedParam);
    if (s) navigate(s.kind === "ext" ? `/jobs/ext/${s.source}/${s.id}` : `/jobs/${s.slug}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutLoaded, splitActive, selectedParam]);

  // the results list must not refetch when only the selected job changes
  const listKey = useMemo(() => {
    const p = new URLSearchParams(searchParams);
    p.delete("selected");
    return p.toString();
  }, [searchParams]);

  const fetchJobs = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    const sp = new URLSearchParams(listKey);
    const params = { page, limit };
    ["query", "location", "category", "job_type", "workplace", "source", "radius",
      "salary_min", "salary_max", "salary_period", "salary_only", "sort"].forEach((k) => {
      const v = sp.get(k);
      if (v) params[k] = v;
    });
    api.get("/jobs", { params })
      .then((res) => { setData(res.data); setLoadError(false); })
      .catch(() => setLoadError(true))
      .finally(() => { if (!silent) setLoading(false); });
  }, [listKey, page, limit]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);
  useEffect(() => {
    if (loading) return;
    try {
      const raw = sessionStorage.getItem("ht_jobs_scroll");
      if (!raw) return;
      const { key, y } = JSON.parse(raw);
      if (key !== window.location.search || y < 200 || !(data.items || []).length) return;
      sessionStorage.removeItem("ht_jobs_scroll");
      let tries = 0;
      const go = () => {
        // split view: the results pane scrolls independently of the window
        if (listRef.current && selected) listRef.current.scrollTop = y;
        else window.scrollTo({ top: y, behavior: "auto" });
        const cur = listRef.current && selected ? listRef.current.scrollTop : window.scrollY;
        if (Math.abs(cur - y) > 4 && tries++ < 10) setTimeout(go, 120);
      };
      setTimeout(go, 80);
    } catch { /* scroll restore is best-effort */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);
  // totals refresh automatically as listings change
  useEffect(() => {
    const t = setInterval(() => fetchJobs(true), 60000);
    return () => clearInterval(t);
  }, [fetchJobs]);
  // smart search: apply the keyword while typing (debounced). The timer always
  // re-reads the CURRENT URL at fire time, so a clear-all or chip removal that
  // happened while it was pending is never overwritten by a stale closure.
  const queryDebounce = useRef(null);
  useEffect(() => {
    clearTimeout(queryDebounce.current);
    const val = query;
    queryDebounce.current = setTimeout(() => {
      queryDebounce.current = null;
      const cur = new URLSearchParams(window.location.search).get("query") || "";
      if (val.trim() !== cur) setFilter("query", val.trim());
    }, 800);
    return () => clearTimeout(queryDebounce.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  // location applies the same way: edit it and the search runs itself after a pause
  const locationDebounce = useRef(null);
  useEffect(() => {
    clearTimeout(locationDebounce.current);
    const val = location;
    locationDebounce.current = setTimeout(() => {
      locationDebounce.current = null;
      const cur = new URLSearchParams(window.location.search).get("location") || "";
      if (val.trim() !== cur) setFilter("location", val.trim());
    }, 800);
    return () => clearTimeout(locationDebounce.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);
  // browser Back/Forward changes the URL without touching the inputs — re-sync the
  // settled inputs (never mid-typing, while a debounce timer is pending)
  useEffect(() => {
    if (!queryDebounce.current) setQuery(searchParams.get("query") || "");
    if (!locationDebounce.current) setLocation(searchParams.get("location") || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  useEffect(() => {
    if (loading || !(data.items || []).length) return;
    const ids = data.items.slice(0, 40).map((j) => (j.source && j.source !== "native" ? `${j.source}:${j.id}` : j.id));
    const key = ids.join(",");
    if (imprKey.current === key) return;
    imprKey.current = key;
    const p = new URLSearchParams(window.location.search);
    api.post("/track/impressions", {
      ids,
      utm_source: p.get("utm_source") || "", utm_medium: p.get("utm_medium") || "",
      utm_campaign: p.get("utm_campaign") || "", referer: document.referrer || "",
    }).catch(() => {});
  }, [data, loading]);
  useEffect(() => { api.get("/jobs/meta").then((res) => setMeta(res.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/jobs/facets").then((res) => setFacets(res.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/jobs/areas").then((res) => setAreas(res.data.areas || [])).catch(() => {}); }, []);
  // remember the exact filter/page state so detail pages can return to it (never
  // include the split-view selection — on phones that link redirects to the job page)
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      p.delete("selected");
      const s = p.toString();
      sessionStorage.setItem("jobs:return", s ? `?${s}` : "");
    } catch (_) { /* best-effort */ }
  }, [searchParams]);

  const applyFilters = (e) => {
    if (e) e.preventDefault();
    // everything applies at once — only when the user presses Search
    const params = new URLSearchParams(searchParams);
    if (query.trim()) params.set("query", query.trim()); else params.delete("query");
    if (location.trim()) params.set("location", location.trim()); else params.delete("location");
    if (fCategory) params.set("category", fCategory); else params.delete("category");
    if (fJobType) params.set("job_type", fJobType); else params.delete("job_type");
    if (fWorkplace) params.set("workplace", fWorkplace); else params.delete("workplace");
    // radius is meaningless without somewhere to measure from
    if (fRadius && location.trim()) params.set("radius", fRadius); else params.delete("radius");
    if (fSalaryMin || fSalaryMax) {
      params.set("salary_period", fSalaryPeriod);
      if (fSalaryMin) params.set("salary_min", fSalaryMin); else params.delete("salary_min");
      if (fSalaryMax) params.set("salary_max", fSalaryMax); else params.delete("salary_max");
    } else {
      params.delete("salary_min"); params.delete("salary_max"); params.delete("salary_period");
    }
    if (fSalaryOnly) params.set("salary_only", "1"); else params.delete("salary_only");
    if (fSort) params.set("sort", fSort); else params.delete("sort");
    params.delete("page");
    // keep "selected": refining the search must not close the split view's open job
    setSearchParams(params);
    setFiltersOpen(false);
    // in split view the page is locked and results live inside the left pane — no page jump
    if (!splitOn) setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
  };

  const setFilter = (key, value) => {
    // build from the live URL, never a render-time closure: debounced timers and
    // chip removals must compose on the latest state, not the one they captured
    const params = new URLSearchParams(window.location.search);
    if (value) params.set(key, value); else params.delete(key);
    params.delete("page");
    // keep "selected": filtering must never drop the job open in the split pane
    setSearchParams(params);
  };

  const pickSource = (v) => {
    setFilter("source", v);
  };

  const goPage = (p) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", String(p));
    setSearchParams(params);
    // in split view the results pane scrolls; otherwise the page
    if (listRef.current && selected) listRef.current.scrollTo({ top: 0, behavior: "smooth" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // applied-filter chips are built ONLY from the URL — a filter the user never
  // confirmed never shows as active
  const PERIOD_SHORT = { hour: "hr", day: "day", week: "wk", month: "mo", year: "yr" };
  const SORT_LABELS = { newest: "Newest first", salary: "Highest salary", distance: "Closest distance" };
  const chips = (() => {
    const out = [];
    const g = (k) => searchParams.get(k);
    if (g("query")) out.push({ id: "query", label: `“${g("query")}”`, keys: ["query"], local: () => setQuery("") });
    if (g("location")) out.push({ id: "location", label: g("location"), keys: ["location"], local: () => setLocation("") });
    if (g("category")) out.push({ id: "category", label: g("category"), keys: ["category"] });
    if (g("job_type")) out.push({ id: "job-type", label: g("job_type"), keys: ["job_type"] });
    if (g("workplace")) out.push({ id: "workplace", label: g("workplace"), keys: ["workplace"] });
    if (g("radius")) out.push({ id: "radius", label: `Within ${g("radius")} miles`, keys: ["radius"] });
    if (g("salary_min") || g("salary_max")) {
      const lo = g("salary_min") ? `£${Number(g("salary_min")).toLocaleString("en-GB")}` : "£0";
      const hi = g("salary_max") ? `£${Number(g("salary_max")).toLocaleString("en-GB")}` : "any";
      out.push({ id: "salary", label: `${lo}–${hi}/${PERIOD_SHORT[g("salary_period") || "year"]}`, keys: ["salary_min", "salary_max", "salary_period"] });
    }
    if (g("salary_only")) out.push({ id: "salary-only", label: "Salary listed", keys: ["salary_only"] });
    if (g("sort")) out.push({ id: "sort", label: SORT_LABELS[g("sort")] || g("sort"), keys: ["sort"] });
    return out;
  })();
  const removeChip = (chip) => {
    const params = new URLSearchParams(window.location.search);
    chip.keys.forEach((k) => params.delete(k));
    params.delete("page");
    if (chip.local) chip.local();
    setSearchParams(params);
  };

  // ONE filter form, rendered inside the focused filters modal on every screen size —
  // never duplicated. Fields are modal-local; the search updates only on Search.
  const filtersForm = (
      <form id="jobs-filter-form" onSubmit={applyFilters} data-testid="jobs-filter-form" className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <input data-testid="filter-keywords" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Keyword"
            className="w-full h-10 sm:h-11 pl-10 pr-3 border border-zinc-300 rounded-md text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white" />
        </div>
        <LocationInput value={location} onChange={setLocation} testid="filter-location" placeholder="Location" liveLocations={facets.locations} />
        <DepartmentInput value={fCategory}
          onChange={setFCategory}
          categories={(facets.categories.length ? facets.categories : meta.categories).slice().sort()}
          counts={facets.category_counts || {}} testid="filter-category" />
        <select data-testid="filter-job-type" value={fJobType}
          onChange={(e) => setFJobType(e.target.value)}
          className="h-10 sm:h-11 px-3 border border-zinc-300 rounded-md text-base sm:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 text-zinc-700">
          <option value="">Any employment type</option>
          <option value="Full-time">Full-time</option>
          <option value="Part-time">Part-time</option>
          <option value="Permanent">Permanent</option>
          <option value="Temporary">Temporary</option>
          <option value="Contract">Contract</option>
          <option value="Apprenticeship">Apprenticeship</option>
        </select>

        {/* Advanced filters: optional, collapsible, applied only on Search */}
        <div className="sm:col-span-2 border-t border-zinc-100 pt-3">
          <button type="button" onClick={() => setAdvOpen((v) => !v)} data-testid="advanced-filters-toggle" aria-expanded={advOpen}
            className="flex w-full items-center justify-between text-sm font-semibold text-zinc-800 hover:text-blue-700 transition-colors duration-200">
            <span className="inline-flex items-center gap-2">
              Advanced filters
              {advActiveCount > 0 && (
                <span className="min-w-[16px] h-4 px-1 bg-red-600 text-white text-[10px] font-bold rounded-full inline-flex items-center justify-center" data-testid="advanced-active-count">{advActiveCount}</span>
              )}
            </span>
            {advOpen ? <ChevronUp className="h-4 w-4 text-zinc-400" /> : <ChevronDown className="h-4 w-4 text-zinc-400" />}
          </button>
          {advOpen && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3" data-testid="advanced-filters">
              <select data-testid="filter-workplace" value={fWorkplace} onChange={(e) => setFWorkplace(e.target.value)}
                className="h-10 sm:h-11 px-3 border border-zinc-300 rounded-md text-base sm:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 text-zinc-700">
                <option value="">Any work arrangement</option>
                <option value="On-site">On-site</option>
                <option value="Hybrid">Hybrid</option>
                <option value="Remote">Remote</option>
              </select>
              <div>
                <select data-testid="filter-radius" value={fRadius} onChange={(e) => setFRadius(e.target.value)} disabled={!location.trim()}
                  className="w-full h-10 sm:h-11 px-3 border border-zinc-300 rounded-md text-base sm:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 text-zinc-700 disabled:opacity-50 disabled:bg-zinc-50">
                  <option value="">Any distance</option>
                  {[5, 10, 15, 25, 50].map((n) => <option key={n} value={n}>Within {n} miles</option>)}
                </select>
                {!location.trim() && <p className="mt-1 text-[11px] text-zinc-400">Enter a location above to filter by distance.</p>}
              </div>
              <div className="sm:col-span-2 border border-zinc-200 rounded-md p-3 bg-zinc-50/60">
                <p className="text-xs font-semibold text-zinc-600 mb-2">Salary range</p>
                <div className="grid grid-cols-3 gap-2">
                  <select data-testid="filter-salary-period" value={fSalaryPeriod} onChange={(e) => setFSalaryPeriod(e.target.value)} aria-label="Pay period"
                    className="h-10 px-2 border border-zinc-300 rounded-md text-base sm:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 text-zinc-700">
                    <option value="hour">per hour</option>
                    <option value="day">per day</option>
                    <option value="week">per week</option>
                    <option value="month">per month</option>
                    <option value="year">per year</option>
                  </select>
                  <input type="number" min="0" inputMode="numeric" placeholder="Min £" data-testid="filter-salary-min"
                    value={fSalaryMin} onChange={(e) => setFSalaryMin(e.target.value)}
                    className="h-10 px-3 border border-zinc-300 rounded-md text-base sm:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  <input type="number" min="0" inputMode="numeric" placeholder="Max £" data-testid="filter-salary-max"
                    value={fSalaryMax} onChange={(e) => setFSalaryMax(e.target.value)}
                    className="h-10 px-3 border border-zinc-300 rounded-md text-base sm:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600" />
                </div>
                {(fSalaryMin || fSalaryMax) && (
                  <p className="mt-1.5 text-[11px] text-zinc-400" data-testid="salary-period-hint">
                    Only {{ hour: "hourly", day: "daily", week: "weekly", month: "monthly", year: "annual" }[fSalaryPeriod]} salaries in this range match — pay periods are never mixed.
                  </p>
                )}
                <label className="mt-2 flex items-center gap-2 text-xs text-zinc-600 cursor-pointer">
                  <input type="checkbox" checked={fSalaryOnly} onChange={(e) => setFSalaryOnly(e.target.checked)}
                    data-testid="filter-salary-only" className="h-4 w-4 accent-zinc-900" />
                  Only show jobs with a listed salary
                </label>
              </div>
              <div className="sm:col-span-2">
                <select data-testid="filter-sort" value={fSort} onChange={(e) => setFSort(e.target.value)} aria-label="Sort results"
                  className="w-full h-10 sm:h-11 px-3 border border-zinc-300 rounded-md text-base sm:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 text-zinc-700">
                  <option value="">Sort: Most relevant</option>
                  <option value="newest">Sort: Newest first</option>
                  <option value="salary">Sort: Highest salary</option>
                  <option value="distance">Sort: Closest distance</option>
                </select>
                {fSort === "distance" && !location.trim() && (
                  <p className="mt-1 text-[11px] text-zinc-400">Closest distance needs a location to measure from.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </form>
  );
  return (
    <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="jobs-page">
      <PageGlow />
      <Seo
        title="Search UK Jobs Near You | HiredToday"
        description="Search current UK jobs near you by role, location and work type. Free to browse and apply on HiredToday."
        path="/jobs"
      />
      <div className="mb-8">
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-black text-zinc-950">Find Work That Fits.</h1>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
          <p className="text-zinc-600" data-testid="jobs-greeting">
            {(() => { const total = liveTotal || (!hasFilters ? data.total : 0);
              if (!total && loading) return <span className="text-zinc-400">Counting today's live jobs…</span>;
              return user ? (
              <><span className="text-blue-600 font-semibold" data-testid="jobs-greeting-name">{user.name?.split(" ")[0]}</span>, explore {total.toLocaleString("en-GB")} live jobs.</>
            ) : (
              <>Explore {total.toLocaleString("en-GB")} live jobs.</>
            ); })()}
            {hasFilters && !loading && <span className="text-zinc-400"> {data.total} match your search.</span>}
          </p>
          {/* one compact toolbar row at every width: Filters → Alerts → Reset → Per page */}
          <div className="order-3 flex w-full flex-nowrap items-center gap-2 whitespace-nowrap" data-testid="jobs-controls">
            <button type="button" onClick={() => setFiltersOpen(true)} data-testid="filters-toggle" aria-expanded={filtersOpen} aria-haspopup="dialog"
              className="inline-flex shrink-0 items-center gap-1.5 h-8 px-2.5 border border-zinc-300 rounded-md text-xs font-semibold text-zinc-700 bg-white hover:border-zinc-500 transition-colors duration-200">
              <SlidersHorizontal className="h-3.5 w-3.5" /> Filters
              {activeFilterCount > 0 && (
                <span className="min-w-[16px] h-4 px-1 bg-red-600 text-white text-[10px] font-bold rounded-full inline-flex items-center justify-center" data-testid="filters-active-count">{activeFilterCount}</span>
              )}
            </button>
            <JobAlertControl compact label="Alerts" className="relative inline-block shrink-0"
              query={searchParams.get("query") || ""}
              location={searchParams.get("location") || ""}
              category={searchParams.get("category") || ""}
              jobType={searchParams.get("job_type") || ""}
              workplace={searchParams.get("workplace") || ""}
            />
            {hasFilters && (
              <button type="button" onClick={resetFilters} data-testid="filters-reset"
                className="inline-flex shrink-0 items-center h-8 text-xs text-zinc-400 underline underline-offset-2 hover:text-zinc-600 transition-colors duration-200 whitespace-nowrap">Reset</button>
            )}
            <label className="inline-flex shrink-0 items-center gap-1.5 text-xs text-zinc-500 whitespace-nowrap">
              <span className="max-[479px]:hidden">Per page</span>
              <select aria-label="Jobs per page" data-testid="filter-page-size" value={searchParams.get("limit") || "25"}
                onChange={(e) => setFilter("limit", e.target.value)}
                className="h-8 px-1.5 sm:px-2 border border-zinc-300 rounded-md text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 text-zinc-700">
                {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          </div>
          {chips.length > 0 && (
            <div className="order-4 flex w-full flex-wrap items-center gap-1.5" data-testid="active-filters">
              {chips.map((c) => (
                <button key={c.id} type="button" onClick={() => removeChip(c)} data-testid={`chip-${c.id}`}
                  className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1.5 rounded-full bg-blue-50 border border-blue-200 text-xs font-medium text-blue-800 hover:border-blue-400 transition-colors duration-200">
                  {c.label} <X className="h-3 w-3 text-blue-400" />
                </button>
              ))}
              <button type="button" onClick={resetFilters} data-testid="clear-all-filters"
                className="h-7 px-2 text-xs font-semibold text-zinc-400 underline underline-offset-2 hover:text-zinc-700 transition-colors duration-200">
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {(() => {
        const resultsBody = (
          <>
      <div className="relative scroll-mt-24" ref={resultsRef}>
        {loading && (
          <div className="absolute inset-0 z-10 flex items-start justify-center rounded-md bg-white/60 backdrop-blur-[3px]" data-testid="jobs-loading">
            <div className="sticky top-24 text-center px-6 pt-16 pb-10">
              <div className="h-10 w-10 border-[3px] border-zinc-200 border-t-zinc-900 rounded-full animate-spin mx-auto" />
              <p className="mt-5 font-display text-lg font-bold text-zinc-950">Finding the best matches for you…</p>
              <p className="mt-1.5 text-sm text-zinc-500 max-w-xs mx-auto">Our engines are searching thousands of live UK roles right now.</p>
            </div>
          </div>
        )}
        {data.corrected && (
          <p className="mb-4 text-sm text-zinc-500" data-testid="jobs-corrected-hint">
            Showing results for <strong className="text-zinc-800">{data.corrected}</strong>
          </p>
        )}
        {!loading && (data.notes || []).length > 0 && (
          <div className="mb-4 space-y-1" data-testid="jobs-filter-notes">
            {(data.notes || []).map((n, i) => (
              <p key={i} className="text-xs text-zinc-500 flex items-start gap-1.5" data-testid={`jobs-filter-note-${i}`}>
                <Info className="h-3.5 w-3.5 mt-px shrink-0 text-zinc-400" /> {n}
              </p>
            ))}
          </div>
        )}
        {loadError ? (
          <div className="border border-dashed border-amber-300 bg-amber-50 rounded-md p-12 text-center" data-testid="jobs-load-error">
            <p className="font-display text-xl font-bold text-zinc-900">Search is temporarily unavailable.</p>
            <p className="mt-2 text-sm text-zinc-600">Please try again in a moment — your search and filters are kept.</p>
            <button onClick={() => fetchJobs()} data-testid="jobs-retry-button"
              className="mt-4 px-5 py-2.5 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">
              Try again
            </button>
          </div>
        ) : loading && data.items.length === 0 ? (
          <div className="border border-zinc-200 rounded-md p-28 bg-zinc-50/50" data-testid="jobs-loading-space" />
        ) : data.items.length === 0 ? (
        (() => {
          return (
            <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center" data-testid="jobs-no-results">
              {hasFilters ? (
                <>
                  <p className="font-display text-xl font-bold text-zinc-900">Nothing matches that search yet.</p>
                  <p className="mt-2 text-sm text-zinc-500">Try a broader keyword, a nearby town, or clear the filters.</p>
                </>
              ) : (
                <>
                  <p className="font-display text-xl font-bold text-zinc-900">No jobs live yet. Check back soon.</p>
                  <p className="mt-2 text-sm text-zinc-500">New roles land here first.</p>
                </>
              )}
            </div>
          );
        })()
        ) : (
          <div className={`grid grid-cols-1 gap-4 transition-opacity duration-200 ${loading ? "opacity-50 pointer-events-none select-none" : ""}`} data-testid="jobs-results">
            {data.items.map((job, i) => (
              <JobCard key={job.id} job={job} index={i}
                onSelect={splitActive ? selectJob : null}
                selected={!!selected && (selected.kind === "ext"
                  ? job.source === selected.source && String(job.id) === String(selected.id)
                  : job.slug === selected.slug)} />
            ))}
          </div>
        )}
      </div>

      {data.pages > 1 && (
        <div className="mt-10 flex items-center justify-center gap-3" data-testid="jobs-pagination">
          <button disabled={page <= 1} onClick={() => goPage(page - 1)} data-testid="pagination-prev"
            className="inline-flex items-center gap-1 px-4 py-2 border border-zinc-300 rounded-md text-sm font-medium disabled:opacity-40 hover:border-zinc-400 transition-colors duration-200">
            <ChevronLeft className="h-4 w-4" /> Previous
          </button>
          <span className="text-sm text-zinc-500">Page {data.page} of {data.pages}</span>
          <button disabled={page >= data.pages} onClick={() => goPage(page + 1)} data-testid="pagination-next"
            className="inline-flex items-center gap-1 px-4 py-2 border border-zinc-300 rounded-md text-sm font-medium disabled:opacity-40 hover:border-zinc-400 transition-colors duration-200">
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
          </>
        );
        // split view: a fixed workspace covering the entire viewport below the sticky site
        // header. The background is an OPAQUE light gradient in the HiredToday hero colours
        // (soft blue/yellow/white) with gentle static edge glows — nothing behind it can
        // show through, underneath or around it. The page is scroll-locked (see the splitOn
        // effect); both panes scroll independently with overscroll contained.
        if (!(splitActive && selected)) return resultsBody;
        return (
          <div className="lg:fixed lg:inset-x-0 lg:top-16 lg:bottom-0 lg:z-30" data-testid="split-view"
            style={{ background: "linear-gradient(155deg, #EFF6FF 0%, #F8FAFF 32%, #FFFFFF 55%, #FFFDF5 78%, #FFFBEB 100%)" }}>
            {/* soft hero-colour glows at the edges only — static (reduced-motion safe),
                centre stays light for contrast */}
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden hidden lg:block">
              <div className="absolute rounded-full blur-3xl opacity-40" style={{ background: "radial-gradient(closest-side, #BFDBFE, transparent)", width: "44vw", height: "44vw", top: "-18vw", left: "-12vw" }} />
              <div className="absolute rounded-full blur-3xl opacity-35" style={{ background: "radial-gradient(closest-side, #FDE68A, transparent)", width: "38vw", height: "38vw", top: "-14vw", right: "-10vw" }} />
              <div className="absolute rounded-full blur-3xl opacity-30" style={{ background: "radial-gradient(closest-side, #DBEAFE, transparent)", width: "34vw", height: "34vw", bottom: "-16vw", right: "12vw" }} />
              <div className="absolute rounded-full blur-3xl opacity-25" style={{ background: "radial-gradient(closest-side, #FEF3C7, transparent)", width: "26vw", height: "26vw", bottom: "-10vw", left: "-8vw" }} />
            </div>
            <div className="relative h-full max-w-7xl mx-auto lg:px-6 lg:pt-3 lg:pb-4 flex flex-col">
              {/* sticky-in-workspace toolbar: compact Filters shortcut opens the existing filter
                  panel as an overlay — same form, same state; search, selection and pane
                  scroll positions all stay put */}
              <div className="hidden lg:flex items-center gap-3 h-9 mb-3 shrink-0" data-testid="split-toolbar">
                <button type="button" onClick={() => setFiltersOpen((v) => !v)} data-testid="split-filters-toggle" aria-expanded={filtersOpen}
                  className="inline-flex items-center gap-1.5 h-8 px-3 border border-zinc-200/80 rounded-md text-xs font-semibold text-zinc-700 bg-white/80 backdrop-blur-sm hover:border-zinc-400 transition-colors duration-200 shadow-sm">
                  <SlidersHorizontal className="h-3.5 w-3.5" /> Filters
                  {activeFilterCount > 0 && (
                    <span className="min-w-[16px] h-4 px-1 bg-red-600 text-white text-[10px] font-bold rounded-full inline-flex items-center justify-center" data-testid="split-filters-count">{activeFilterCount}</span>
                  )}
                </button>
                <span className="text-xs text-zinc-500" data-testid="split-result-count">{data.total.toLocaleString("en-GB")} results</span>
                {chips.length > 0 && (
                  <div className="flex items-center gap-1.5 overflow-x-auto min-w-0 flex-1" data-testid="split-active-filters">
                    {chips.map((c) => (
                      <button key={c.id} type="button" onClick={() => removeChip(c)} data-testid={`split-chip-${c.id}`}
                        className="shrink-0 inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded-full bg-blue-50/90 border border-blue-200 text-[11px] font-medium text-blue-800 hover:border-blue-400 transition-colors duration-200">
                        {c.label} <X className="h-2.5 w-2.5 text-blue-400" />
                      </button>
                    ))}
                    <button type="button" onClick={resetFilters} data-testid="split-clear-all-filters"
                      className="shrink-0 h-6 px-1.5 text-[11px] font-semibold text-zinc-400 underline underline-offset-2 hover:text-zinc-700 transition-colors duration-200">
                      Clear all
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:grid-rows-[minmax(0,1fr)] gap-5 flex-1 min-h-0">
                <div ref={listRef} data-testid="split-results"
                  onScroll={(e) => { try { sessionStorage.setItem("ht_jobs_scroll", JSON.stringify({ key: window.location.search, y: e.currentTarget.scrollTop })); } catch { /* best-effort */ } }}
                  className="lg:h-full lg:min-h-0 lg:overflow-y-auto lg:overscroll-contain rounded-xl border border-zinc-200/70 bg-white/75 backdrop-blur-md shadow-[0_8px_30px_-12px_rgba(30,64,175,0.08)] lg:p-3">
                  {resultsBody}
                </div>
                <div className="lg:h-full lg:min-h-0 lg:overflow-y-auto lg:overscroll-contain border border-zinc-200/70 rounded-xl bg-white/85 backdrop-blur-xl shadow-[0_16px_50px_-16px_rgba(30,64,175,0.14)] p-5"
                  data-testid="split-detail" key={selectedParam}>
                  {selected.kind === "ext"
                    ? <ExtJobDetail embedded embeddedSource={selected.source} embeddedId={selected.id} onClear={clearSelected} />
                    : <JobDetail embedded embeddedSlug={selected.slug} onClear={clearSelected} />}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="mt-10 flex flex-wrap items-center justify-center gap-2 border-t border-zinc-200 pt-8" data-testid="filter-source-row">
        <span className="text-xs text-zinc-500 mr-1">Source:</span>
        {[["", "All sources"], ["native", "HiredToday"], ["adzuna", "Adzuna"], ["reed", "Reed"], ["govuk", "GOV.UK Apprenticeships"], ["cvlibrary", "CV-Library"], ["nhs", "NHS Jobs"]].map(([v, l]) => (
          <button key={v || "all"} onClick={() => pickSource(v)} data-testid={`filter-source-${v || "all"}`}
            className={`h-8 px-3.5 rounded-full text-xs font-semibold border transition-colors duration-200 ${
              (searchParams.get("source") || "") === v
                ? "bg-zinc-950 text-white border-zinc-950"
                : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500"}`}>
            {l}
          </button>
        ))}
      </div>


      {areas.length > 0 && (
        <div className="mt-14 border-t border-zinc-200 pt-8" data-testid="areas-strip">
          <button onClick={() => setShowAllAreas((v) => !v)} data-testid="areas-toggle" aria-expanded={showAllAreas}
            className="flex items-center gap-2 font-display text-lg font-bold text-zinc-950 hover:text-blue-700 transition-colors duration-200">
            Browse jobs by area
            <span className="text-xs font-medium text-zinc-400">({areas.length} areas)</span>
            {showAllAreas ? <ChevronUp size={16} className="text-zinc-400" /> : <ChevronDown size={16} className="text-zinc-400" />}
          </button>
          {!showAllAreas && (
            <div className="flex flex-wrap gap-2 mt-4">
              {areas.slice(0, 14).map((a) => {
                const slug = a.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
                return (
                  <Link key={a} to={`/jobs/near/${slug}`} data-testid={`area-link-${slug}`}
                    className="px-3 py-1.5 border border-zinc-200 rounded-full text-xs font-medium text-zinc-700 hover:border-zinc-400 hover:text-zinc-950 transition-colors duration-200">
                    Jobs near {a}
                  </Link>
                );
              })}
              {areas.length > 14 && (
                <button onClick={() => setShowAllAreas(true)} data-testid="areas-show-all"
                  className="px-3 py-1.5 border border-dashed border-zinc-300 rounded-full text-xs font-semibold text-blue-700 hover:border-blue-400 transition-colors duration-200">
                  +{areas.length - 14} more areas
                </button>
              )}
            </div>
          )}
          {showAllAreas && (
            <div className="flex flex-wrap gap-2 mt-4">
              {areas.map((a) => {
                const slug = a.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
                return (
                  <Link key={a} to={`/jobs/near/${slug}`} data-testid={`area-link-${slug}`}
                    className="px-3 py-1.5 border border-zinc-200 rounded-full text-xs font-medium text-zinc-700 hover:border-zinc-400 hover:text-zinc-950 transition-colors duration-200">
                    Jobs near {a}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Filters open as a focused modal — centred on desktop, bottom sheet on mobile.
          The page behind blurs and darkens slightly and stays scroll-locked; the single
          existing form is reused, never duplicated. Closes via X, Escape, backdrop or
          Search, and the page is restored exactly as it was. */}
      {filtersOpen && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center sm:p-6" data-testid="filters-modal" role="dialog" aria-modal="true" aria-label="Search filters">
          <button type="button" aria-label="Close filters" data-testid="filters-modal-backdrop" onClick={() => setFiltersOpen(false)}
            className="absolute inset-0 bg-zinc-950/35 backdrop-blur-sm" />
          <div className="relative w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-xl border border-zinc-200/80 shadow-2xl max-h-[88vh] sm:max-h-[85vh] flex flex-col overflow-hidden" data-testid="filters-modal-panel">
            <div className="shrink-0 flex items-center justify-between px-5 sm:px-6 pt-5 sm:pt-6 pb-3.5 border-b border-zinc-100">
              <h2 className="font-display text-lg font-bold text-zinc-950">Filter jobs</h2>
              <button type="button" onClick={() => setFiltersOpen(false)} data-testid="filters-modal-close" aria-label="Close filters"
                className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors duration-200">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 sm:px-6 py-4">
              {filtersForm}
            </div>
            <div className="shrink-0 flex items-center gap-2 px-5 sm:px-6 pt-3.5 border-t border-zinc-100 bg-white rounded-b-2xl sm:rounded-b-xl"
              style={{ paddingBottom: "max(0.875rem, env(safe-area-inset-bottom))" }}>
              <button type="button" onClick={clearFilterForm} data-testid="filters-clear"
                className="h-10 px-3.5 text-xs font-semibold text-zinc-500 hover:text-zinc-800 border border-zinc-300 rounded-md hover:border-zinc-500 transition-colors duration-200">
                Clear
              </button>
              <button type="button" onClick={() => setFiltersOpen(false)} data-testid="filters-cancel"
                className="h-10 px-3.5 text-xs font-semibold text-zinc-500 hover:text-zinc-800 border border-zinc-300 rounded-md hover:border-zinc-500 transition-colors duration-200">
                Cancel
              </button>
              <button type="submit" form="jobs-filter-form" data-testid="filter-submit"
                className="ml-auto h-10 px-6 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">
                Search
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
